<template>
  <v-col cols="12" class="pa-0">
    <v-row no-gutters>
      <v-col cols="auto" class="objects-input-value">
        {{ computedTitle }}
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-icon v-if="activeAddingTool === 'addByMap'" size="20px" color="error" @click="toggleOffAddingByMap">
          mdi-close
        </v-icon>
      </v-col>
      <v-col cols="auto">
        <dots-menu
          v-if="!isSomeAddingToolActive && isEditingOn && !hasMaxNumberOfObjects"
          :items="dotsMenuItems"
          @addByMap="addByMap"
          @addByIds="addByIds"
          @addByNumber="addByProperty"
          @addByIntersection="addByIntersection"
        ></dots-menu>
      </v-col>
    </v-row>
    <v-row no-gutters class="objects-input-hint">
      <v-col cols="auto">
        {{ computedHint }}
      </v-col>
    </v-row>
    <div class="objects-added-list">
      <v-row no-gutters v-for="(object, idx) of computedValue" :key="idx">
        <sidebar-object-added
          :property-name="propertyName"
          :property-translation="propertyTranslation"
          :value="object"
          :layer-name="object.layer_name ? object.layer_name : computedNewObjectName(object)"
          :has-permissions="
            object.does_user_have_permission_for_layer ? object.does_user_have_permission_for_layer : true
          "
          :is-new-instance="isNewInstance"
          @deleteObject="deleteObject(idx)"
          :readonly="!isEditingOn"
        />
      </v-row>
    </div>
    <v-row no-gutters align="baseline" v-if="activeAddingTool === 'addByIds'">
      <v-col cols="4">
        <data-input
          :data-type="{ name: 'select' }"
          :items="filteredLayers"
          item-text="name"
          item-value="id"
          v-model="newObject.layer_id"
          class="mt-0"
        ></data-input>
      </v-col>
      <v-col cols="auto" class="sidebar-object-added-text mr-3 pl-1">
        {{ ':' }}
      </v-col>
      <v-col cols="auto" class="sidebar-object-added-text mr-1">
        {{ 'ID' }}
      </v-col>
      <v-col cols="2">
        <data-input class="mt-0" hide-details dense v-model="newObject.object_id" :data-type="dataType" />
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-icon
          size="20px"
          :color="isAddObjectDisabled ? '#bababa' : 'success'"
          @click="isAddObjectDisabled ? null : addObjectById()"
          >mdi-check</v-icon
        >
      </v-col>
      <v-col cols="auto">
        <v-icon size="20px" color="error" @click="toggleOffAddingById">mdi-close</v-icon>
      </v-col>
    </v-row>
    <v-row no-gutters align="baseline" v-if="activeAddingTool === 'addByProperty'">
      <v-col cols="4">
        <data-input
          :data-type="{ name: 'select' }"
          :items="filteredLayersByDataSource"
          item-text="name"
          return-object
          v-model="newObject.layer"
          class="mt-0"
        ></data-input>
      </v-col>
      <v-col cols="auto" class="sidebar-object-added-text mr-3 pl-1">
        {{ ':' }}
      </v-col>
      <v-col cols="auto" class="sidebar-object-added-text mr-1">
        {{ propertyTranslation }}
      </v-col>
      <v-col cols="3">
        <data-input class="mt-0" hide-details dense v-model="newObject[propertyName]" :data-type="propertyDatatype" />
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-icon size="20px" color="success" @click="addObjectByProperty">mdi-check</v-icon>
      </v-col>
      <v-col cols="auto">
        <v-icon size="20px" color="error" @click="toggleOffAddingById">mdi-close</v-icon>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { call, get } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';
import SidebarObjectAdded from '@/components/SidebarObjectAdded';
import DataInput from '@/components/DataInput.vue';

export default {
  name: 'SidebarObjectsInput',
  components: {
    DotsMenu,
    SidebarObjectAdded,
    DataInput,
  },
  props: {
    hint: {
      type: String,
      default: '',
    },
    dataSourceScope: {
      type: String,
      default: '',
    },
    propertyName: {
      type: String,
      default: 'object_id',
    },
    propertyDatatype: {
      type: Object,
      default: () => {
        return {
          name: 'integer',
        };
      },
    },
    propertyTranslation: {
      type: String,
      default: 'ID',
    },
    propertyDotsObject: {
      type: Object,
      default: () => {
        return { name: 'addByIds' };
      },
    },
    value: {
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    isNewInstance: {
      type: Boolean,
      default: false,
    },
    isEditingOn: {
      type: Boolean,
      default: false,
    },
    maxNumberOfObjects: {
      type: Number,
    },
    allowedLayersIds: {
      type: Array,
    },
    translationPath: {
      type: String,
      default: 'dialog.assignObjects',
    },
    isValid: {
      type: Boolean,
      default: false,
    },
  },
  data: vm => ({
    activeAddingTool: '',
    newObject: {},
    dotsMenuItems: [
      {
        name: 'addByMap',
      },
      vm.propertyDotsObject,
      {
        name: 'addByIntersection',
        disabled: true,
      },
    ],
    dataType: {
      name: 'integer',
    },
  }),
  computed: {
    layers: get('layers/layers'),
    dataSources: get('layers/metadata'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    zdmDataSources: get('admin/modulesMapping@zdm_data.datasources'),
    computedDataSources() {
      return Object.values(this.dataSources).filter(
        value => value.scope === this.dataSourceScope && value.geometry_type !== null
      );
    },
    computedValue: {
      get() {
        return this.value || [];
      },
      set(nV) {
        this.$emit('input', nV);
      },
    },
    computedTitle() {
      return this.hasValue ? this.$i18n.t('dialog.assignedObjects') : this.$i18n.t(this.translationPath);
    },
    computedHint() {
      return this.hasValue ? '' : this.hint;
    },
    hasMaxNumberOfObjects() {
      return !!(this.maxNumberOfObjects && this.maxNumberOfObjects <= this.computedValue.length);
    },
    projectLayersIds() {
      return this.projectLayers.map(layer => layer.layer_id);
    },
    filteredLayers() {
      const layersInProject = this.$_objectToArray(this.layers).filter(layer =>
        this.projectLayersIds.includes(layer.id)
      );
      return this.allowedLayersIds
        ? layersInProject.filter(layer => this.allowedLayersIds.includes(layer.id))
        : layersInProject;
    },
    filteredLayersByDataSource() {
      return this.$_objectToArray(this.layers).filter(layer =>
        this.computedDataSources.map(datasource => datasource.name).includes(layer.data_source_name)
      );
    },
    hasValue() {
      return !!(this.computedValue && this.computedValue.length > 0);
    },
    isAddObjectDisabled() {
      if (!this.newObject.layer_id || !this.newObject[this.propertyName]) {
        return true;
      } else {
        return this.isNewObjectAlreadyAdded(this.newObject.layer_id, this.newObject[this.propertyName]);
      }
    },
    isSomeAddingToolActive() {
      return !!this.activeAddingTool;
    },
  },
  methods: {
    getLayerFeature: call('layers/getLayerFeature'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    computedNewObjectName(object) {
      return this.layers[object.layer_id].name;
    },
    addByMap() {
      this.$root.$emit('identification-action', true, { specialType: 'addByMap' });
      this.activeAddingTool = 'addByMap';
    },
    addByIds() {
      this.activeAddingTool = 'addByIds';
    },
    addByProperty() {
      this.activeAddingTool = 'addByProperty';
    },
    addByIntersection() {
      this.activeAddingTool = 'addByIntersection';
    },
    toggleOffAdding() {
      this.toggleOffAddingById();
      this.toggleOffAddingByMap();
      this.toggleOffAddingByProperty();
      this.toggleOffAddingByIntersection();
    },
    toggleOffAddingByMap() {
      this.activeAddingTool = '';
      this.$root.$emit('identification-action', false);
    },
    toggleOffAddingById() {
      this.activeAddingTool = '';
      this.newObject = {};
    },
    toggleOffAddingByProperty() {
      this.activeAddingTool = '';
      this.newObject = {};
    },
    toggleOffAddingByIntersection() {
      this.activeAddingTool = '';
    },
    async addObjectById() {
      try {
        await this.getLayerFeature({ layer_id: this.newObject.layer_id, feature_id: this.newObject.object_id });
        this.addObject(this.newObject.layer_id, this.newObject.object_id, this.newObject.object_id);
        this.toggleOffAddingById();
      } catch {
        this.newObject.object_id = '';
      }
    },
    async addObjectByProperty() {
      try {
        const r = await this.getLayerFeatures({
          layer_id: this.newObject.layer.id,
          features_filter: {
            $EQUAL: {
              [`${this.newObject.layer.data_source_name}.${this.propertyName}`]: `${this.newObject[this.propertyName]}`,
            },
          },
        });
        if (r.data.data.features.features.length === 0) {
          this.$store.set('snackbar/PUSH_MESSAGE!', {
            message: this.$i18n.t('snackbar.notFoundFeatureWithNumber'),
          });
        } else {
          const object_id = r.data.data.features.features[0].id;
          const { display_attribute_name, street_attribute_name, district_attribute_name } = this.$_objectToArray(
            this.zdmDataSources
          ).find(d => d.datasource === this.newObject.layer.data_source_name);
          const street = r.data.data.features.features[0].properties[street_attribute_name];
          const district = r.data.data.features.features[0].properties[district_attribute_name];
          const number = r.data.data.features.features[0].properties[display_attribute_name];
          this.addObject(this.newObject.layer.id, object_id, number, street, district);
        }
        this.toggleOffAddingByProperty();
      } catch {
        this.newObject[this.propertyName] = '';
      }
    },
    addObject(layer_id, object_id, property, street, district) {
      const newObject = {
        layer_id,
        object_id,
        ...(property === undefined ? {} : { [`${this.propertyName}`]: property }),
        ...(street === undefined ? {} : { street }),
        ...(district === undefined ? {} : { district }),
      };
      this.computedValue.push(JSON.parse(JSON.stringify(newObject)));
    },
    deleteObject(idx) {
      this.computedValue.splice(idx, 1);
    },
    isNewObjectAlreadyAdded(layer_id, object_id) {
      return !!this.computedValue.find(object => object.layer_id == layer_id && object.object_id == object_id);
    },
  },
  watch: {
    isEditingOn(nV) {
      if (!nV) {
        this.toggleOffAdding();
      }
    },
    value(nV) {
      this.$emit('update:isValid', nV.length > 0);
    },
  },
  created() {
    this.$emit('update:isValid', this.computedValue.length > 0);
    this.$root.$off('assignedObjects');
    this.$root.$on('assignedObjects', identifiedFeatures => {
      for (const layer of identifiedFeatures) {
        if (this.allowedLayersIds && !this.allowedLayersIds.includes(Number(layer.id))) {
          continue;
        }
        if (layer.id === 'drawingSidebarLayer') {
          continue;
        }
        for (const feature of layer.features) {
          if (!this.isNewObjectAlreadyAdded(feature.layerId, feature.id) && !this.hasMaxNumberOfObjects) {
            this.addObject(feature.layerId, feature.id, feature[this.propertyName], feature.street, feature.district);
          }
        }
      }
      this.toggleOffAddingByMap();
    });
  },
  destroyed() {
    if (this.activeAddingTool === 'addByMap') {
      this.toggleOffAddingByMap();
    }
  },
};
</script>
<style lang="scss" scoped>
.objects-input-hint {
  font-family: Roboto;
  font-style: italic;
  font-weight: 300;
  font-size: 11px;
  line-height: 13px;
  color: rgba(0, 0, 0, 0.59);
  min-height: 13px;
}
.objects-input-value {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
}
.sidebar-object-added-text {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 15px;
  color: rgba(0, 0, 0, 0.7);
}
.objects-added-list {
  max-height: 200px;
  padding-bottom: 8px;
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
